import { useEffect, useState } from 'preact/hooks';
import { CommsAgendaItem, ScheduleView, dateRangeForView } from 'shared/comms-schedule';

type FetchFn = (range: { startDate: string; endDate: string }) => Promise<CommsAgendaItem[]>;

type UseScheduleViewOpts = {
  initialItems: CommsAgendaItem[];
  fetch: FetchFn;
};

// Hook that owns view state + re-fetches the agenda when the view toggles.
// Initial render uses items already loaded by the route loader (Upcoming);
// switching to Sent triggers a client-side fetch.
export function useScheduleView({ initialItems, fetch }: UseScheduleViewOpts) {
  const [view, setView] = useState<ScheduleView>('upcoming');
  const [items, setItems] = useState<CommsAgendaItem[]>(initialItems);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedSent, setHasFetchedSent] = useState(false);

  useEffect(() => {
    if (view === 'upcoming') {
      setItems(initialItems);
      return;
    }
    if (hasFetchedSent) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    fetch(dateRangeForView('sent'))
      .then((next) => {
        if (cancelled) {
          return;
        }
        setItems(next);
        setHasFetchedSent(true);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  return { view, setView, items, isLoading };
}

type ToggleProps = {
  view: ScheduleView;
  onChange: (view: ScheduleView) => void;
};

export function ScheduleViewToggle({ view, onChange }: ToggleProps) {
  return (
    <div class="inline-flex rounded-md border border-gray-300 bg-white p-0.5 text-sm">
      <ToggleOption value="upcoming" current={view} onChange={onChange}>
        Upcoming
      </ToggleOption>
      <ToggleOption value="sent" current={view} onChange={onChange}>
        Sent
      </ToggleOption>
    </div>
  );
}

function ToggleOption({
  value,
  current,
  onChange,
  children,
}: {
  value: ScheduleView;
  current: ScheduleView;
  onChange: (view: ScheduleView) => void;
  children: string;
}) {
  const isActive = value === current;
  return (
    <button
      type="button"
      class={`px-3 py-1 rounded font-medium transition-colors ${
        isActive ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-50'
      }`}
      onClick={() => onChange(value)}
    >
      {children}
    </button>
  );
}
